/**
 * Calendario de nómina.
 *
 * En este hogar conviven dos ritmos: quincenal (15 y último día) y mensual.
 * Sus periodos no coinciden, así que «cuánto nos queda» no tiene una sola
 * respuesta: cada integrante tiene su propio periodo, que va desde el día que
 * cobra hasta el día anterior a su siguiente pago.
 *
 * Cuando la fecha de pago cae en día inhábil, la práctica habitual en México es
 * adelantarla al hábil anterior; por eso `PREVIOUS_BUSINESS_DAY` es el valor
 * por defecto.
 */

import {
  type HolidaySet,
  NO_HOLIDAYS,
  type NonBusinessDayRule,
  adjustToBusinessDay,
} from '../date/businessDays.js'
import {
  type PlainDate,
  addDays,
  addMonths,
  differenceInDays,
  getMonth,
  getYear,
  plainDate,
  toEpochDay,
  withDayOfMonth,
} from '../date/plainDate.js'

/**
 * Cada cuánto entra un ingreso.
 *
 * `IRREGULAR` es el que no tiene calendario —factura, comisiones, venta por su
 * cuenta—. No proyecta fechas ni suma al disponible por adelantado; su valor
 * está en el historial de lo que sí se cobró.
 */
export type PayFrequency = 'SEMI_MONTHLY' | 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'IRREGULAR'

export interface PayrollConfig {
  frequency: PayFrequency
  /**
   * Quincenal: primera ancla del mes, normalmente 15.
   * Mensual: día de pago.
   * `0` significa «último día del mes».
   */
  anchorDayA: number
  /** Solo quincenal: segunda ancla, normalmente `0` (último día). */
  anchorDayB?: number
  /**
   * Solo semanal y catorcenal: fecha de un pago real conocido, desde la que se
   * proyecta el resto. Sin ella no hay forma de fijar la fase del ciclo.
   */
  anchorDate?: PlainDate
  nonBusinessDayRule: NonBusinessDayRule
}

export interface PayPeriod {
  /** Nómina que financia este periodo. */
  payDate: PlainDate
  periodStart: PlainDate
  /** Día anterior al siguiente pago. */
  periodEnd: PlainDate
  nextPayDate: PlainDate
  /** Días naturales que faltan para el siguiente pago, contando hoy. */
  daysRemaining: number
}

const DAYS_PER_STEP: Record<'BIWEEKLY' | 'WEEKLY', number> = {
  BIWEEKLY: 14,
  WEEKLY: 7,
}

function validate(config: PayrollConfig): void {
  // Un ingreso sin calendario no tiene anclas que validar: no las usa.
  if (config.frequency === 'IRREGULAR') return

  const inRange = (d: number): boolean => Number.isInteger(d) && d >= 0 && d <= 31
  if (!inRange(config.anchorDayA)) {
    throw new RangeError(`Día de pago fuera de rango: ${config.anchorDayA}`)
  }
  if (config.frequency === 'SEMI_MONTHLY') {
    if (config.anchorDayB === undefined || !inRange(config.anchorDayB)) {
      throw new RangeError('La nómina quincenal necesita dos días de pago válidos')
    }
  }
  if (
    (config.frequency === 'BIWEEKLY' || config.frequency === 'WEEKLY') &&
    !config.anchorDate
  ) {
    throw new RangeError(
      `La nómina ${config.frequency} necesita una fecha de referencia (anchorDate)`,
    )
  }
}

/**
 * Días de pago de un mes, ya ajustados a día hábil.
 *
 * El ajuste puede empujar una fecha al mes anterior —el día 1 en domingo se
 * paga el viernes previo—, así que el resultado no siempre cae dentro del mes
 * consultado. Quien filtre por rango debe usar `payDatesBetween`.
 */
export function payDatesInMonth(
  year: number,
  month: number,
  config: PayrollConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate[] {
  validate(config)
  const firstOfMonth = plainDate(year, month, 1)
  const anchors: number[] =
    config.frequency === 'SEMI_MONTHLY'
      ? [config.anchorDayA, config.anchorDayB!]
      : [config.anchorDayA]

  const dates = anchors.map((day) =>
    adjustToBusinessDay(
      withDayOfMonth(firstOfMonth, day),
      config.nonBusinessDayRule,
      holidays,
    ),
  )

  return [...new Set(dates)].sort()
}

/**
 * Días de pago en un rango, inclusivo en ambos extremos.
 * Para las frecuencias mensuales explora un mes extra a cada lado, porque el
 * ajuste a día hábil puede mover una fecha fuera de su mes natural.
 */
export function payDatesBetween(
  from: PlainDate,
  to: PlainDate,
  config: PayrollConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate[] {
  // Sin calendario no cae ningún pago en ningún rango. `expectedPayInRange`
  // cuelga de aquí, así que con esta sola línea un ingreso irregular deja de
  // sumar al disponible por adelantado en toda la app.
  if (config.frequency === 'IRREGULAR') return []
  validate(config)
  if (from > to) return []

  if (config.frequency === 'BIWEEKLY' || config.frequency === 'WEEKLY') {
    return periodicPayDatesBetween(from, to, config, holidays)
  }

  const out: PlainDate[] = []
  let anchor = addMonths(plainDate(getYear(from), getMonth(from), 1), -1)
  const lastAnchor = addMonths(plainDate(getYear(to), getMonth(to), 1), 1)

  while (anchor <= lastAnchor) {
    for (const date of payDatesInMonth(getYear(anchor), getMonth(anchor), config, holidays)) {
      if (date >= from && date <= to) out.push(date)
    }
    anchor = addMonths(anchor, 1)
  }

  return [...new Set(out)].sort()
}

function periodicPayDatesBetween(
  from: PlainDate,
  to: PlainDate,
  config: PayrollConfig,
  holidays: HolidaySet,
): PlainDate[] {
  const step = DAYS_PER_STEP[config.frequency as 'BIWEEKLY' | 'WEEKLY']
  const anchor = config.anchorDate!

  // Se retrocede un paso extra: el ajuste a día hábil puede meter en el rango
  // un pago cuya fecha nominal quedaba justo antes.
  const stepsToStart = Math.floor((toEpochDay(from) - toEpochDay(anchor)) / step) - 1
  let cursor = addDays(anchor, stepsToStart * step)

  const out: PlainDate[] = []
  const guard = Math.ceil(differenceInDays(to, from) / step) + 4

  for (let i = 0; i <= guard; i++) {
    const paid = adjustToBusinessDay(cursor, config.nonBusinessDayRule, holidays)
    if (paid >= from && paid <= to) out.push(paid)
    if (cursor > to) break
    cursor = addDays(cursor, step)
  }

  return [...new Set(out)].sort()
}

/**
 * Los próximos `count` días de pago estrictamente posteriores a `from`.
 * Alimenta la generación anticipada de nóminas esperadas y la franja de
 * próximos ingresos del inicio.
 */
export function nextPayDates(
  from: PlainDate,
  count: number,
  config: PayrollConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): PlainDate[] {
  if (count <= 0) return []
  // Sin calendario no hay próximos pagos que proyectar. Devolver una fecha
  // inventada sería peor que no devolver ninguna: la app prometería un dinero
  // en un día que nadie le prometió a quien lo cobra.
  if (config.frequency === 'IRREGULAR') return []
  validate(config)

  const out: PlainDate[] = []
  let windowStart = addDays(from, 1)

  // Se amplía la ventana hasta juntar los pagos pedidos; con nómina mensual
  // bastan `count` meses, pero se deja margen por los ajustes de calendario.
  for (let months = count + 2; out.length < count && months <= count * 3 + 24; months += 6) {
    const windowEnd = addMonths(from, months)
    const found = payDatesBetween(windowStart, windowEnd, config, holidays)
    out.length = 0
    out.push(...found)
  }

  return out.slice(0, count)
}

/**
 * Periodo de gasto vigente en una fecha: desde la última nómina cobrada hasta
 * el día anterior a la siguiente.
 *
 * Es la ventana sobre la que se calcula el dinero disponible de cada persona.
 */
export function currentPayPeriod(
  date: PlainDate,
  config: PayrollConfig,
  holidays: HolidaySet = NO_HOLIDAYS,
): PayPeriod {
  validate(config)

  // Ventana amplia a ambos lados: cubre cualquier frecuencia soportada.
  const windowStart = addMonths(date, -3)
  const windowEnd = addMonths(date, 3)
  const dates = payDatesBetween(windowStart, windowEnd, config, holidays)

  const previous = [...dates].reverse().find((d) => d <= date)
  const next = dates.find((d) => d > date)

  if (!previous || !next) {
    throw new Error(
      `No se pudo ubicar el periodo de nómina en ${date}: revisa la configuración de pago`,
    )
  }

  return {
    payDate: previous,
    periodStart: previous,
    periodEnd: addDays(next, -1),
    nextPayDate: next,
    daysRemaining: differenceInDays(next, date),
  }
}

/**
 * Día en que arranca el periodo del hogar si nadie lo cambia: el mes natural.
 *
 * Es el denominador compartido cuando los integrantes cobran con ritmos
 * distintos —el único marco en el que una quincena y una mensualidad se pueden
 * sumar sin inventar una ventana que no existe para ninguno de los dos— y por
 * eso sigue siendo el valor por omisión.
 */
export const DEFAULT_PERIOD_ANCHOR_DAY = 1

/**
 * Mayor día permitido como arranque de periodo.
 *
 * El 29, el 30 y el 31 no existen todos los meses. La aritmética de aquí los
 * recortaría sin quejarse y las ventanas seguirían siendo contiguas, pero
 * durarían distinto cada mes sin que nada lo explicara en pantalla. Quien cobra
 * a fin de mes tiene su periodo en el día 1, que es lo mismo dicho al derecho.
 */
export const MAX_PERIOD_ANCHOR_DAY = 28

/** Recorta el ancla a un día que exista en cualquier mes. */
export function normalizePeriodAnchorDay(day: number | null | undefined): number {
  if (day === null || day === undefined || !Number.isInteger(day)) {
    return DEFAULT_PERIOD_ANCHOR_DAY
  }
  if (day < 1) return DEFAULT_PERIOD_ANCHOR_DAY
  return Math.min(day, MAX_PERIOD_ANCHOR_DAY)
}

/**
 * Periodo común del hogar: la ventana sobre la que se mide todo el dinero.
 *
 * `anchorDay` es el día del mes en que arranca. Con el valor por omisión —el
 * 1— el periodo es el mes natural y esta función devuelve exactamente lo que
 * devolvía antes de que el ancla existiera. Con cualquier otro, la ventana se
 * desplaza: un hogar que cobra el 25 mide del 25 al 24, que es el mes que de
 * verdad vive. Forzar el mes natural a quien cobra a mediados parte su nómina en
 * dos periodos y hace que el disponible se derrumbe cada día 1 sin que haya
 * pasado nada.
 *
 * El extremo se calcula como «el día anterior al siguiente arranque» y no
 * sumando días: así dos periodos consecutivos siempre encajan sin huecos ni
 * solapes, aunque el ancla caiga en un mes más corto.
 */
export function householdPeriod(
  date: PlainDate,
  anchorDay: number = DEFAULT_PERIOD_ANCHOR_DAY,
): { periodStart: PlainDate; periodEnd: PlainDate } {
  const anchor = normalizePeriodAnchorDay(anchorDay)

  if (anchor === DEFAULT_PERIOD_ANCHOR_DAY) {
    const year = getYear(date)
    const month = getMonth(date)
    return {
      periodStart: plainDate(year, month, 1),
      periodEnd: withDayOfMonth(plainDate(year, month, 1), 0),
    }
  }

  // Se compara contra el arranque **ya resuelto** en este mes, no contra el
  // número. Es la diferencia que importa el 28 de febrero con el ancla en 28:
  // comparar números diría «todavía no llega» y devolvería un periodo que
  // termina ayer.
  const anchorThisMonth = withDayOfMonth(date, anchor)
  const periodStart = date >= anchorThisMonth ? anchorThisMonth : withDayOfMonth(addMonths(date, -1), anchor)

  return {
    periodStart,
    periodEnd: addDays(withDayOfMonth(addMonths(periodStart, 1), anchor), -1),
  }
}

/** Nóminas esperadas dentro de un rango, con su monto neto configurado. */
export interface ExpectedPay {
  date: PlainDate
  amountCents: number
}

export function expectedPayInRange(
  from: PlainDate,
  to: PlainDate,
  config: PayrollConfig,
  netAmountCents: number,
  holidays: HolidaySet = NO_HOLIDAYS,
): ExpectedPay[] {
  return payDatesBetween(from, to, config, holidays).map((date) => ({
    date,
    amountCents: netAmountCents,
  }))
}
